import { type ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { authorityFingerprint } from "../api/authority";
import { useDestinationReference } from "../api/catalogue-hooks";
import type { CurrentActor } from "../api/session-api";
import { useAuth } from "../auth/auth-context";
import { countryName } from "../lib/country-names";
import { CatalogueReferencePicker } from "./catalogue-reference-picker";
import {
  DestinationContext,
  destinationFromLocation,
  destinationHref,
  replaceDestinationContext,
  useDestinationContext,
  validDestinationId,
} from "./destination-context";

export function DestinationWorkspace({ children }: { children: ReactNode }) {
  const { actor } = useAuth();
  if (!actor?.permissions.includes("destinations.read")) return children;
  return (
    <DestinationScope key={authorityFingerprint(actor)} actor={actor}>
      {children}
    </DestinationScope>
  );
}

function DestinationScope({ actor, children }: { actor: CurrentActor; children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const storageKey = `cyra.destination:${authorityFingerprint(actor)}`;
  const fromUrl = destinationFromLocation(
    location.pathname,
    location.search,
    location.state?.returnTo,
  );
  const [rememberedId, setRememberedId] = useState(() => {
    if (fromUrl !== undefined) return fromUrl;
    try {
      return validDestinationId(sessionStorage.getItem(storageKey));
    } catch {
      return "";
    }
  });
  const destinationId = fromUrl ?? rememberedId;
  const destination = useDestinationReference(destinationId);
  useEffect(() => {
    setRememberedId(destinationId);
    try {
      if (destinationId) sessionStorage.setItem(storageKey, destinationId);
      else sessionStorage.removeItem(storageKey);
    } catch {
      /* Storage availability does not block catalogue navigation. */
    }
  }, [destinationId, storageKey]);
  useEffect(() => {
    const contextual = destinationHref(location.pathname + location.search, destinationId);
    if (contextual !== location.pathname + location.search)
      void navigate(contextual, { replace: true, state: location.state });
  }, [destinationId, location.pathname, location.search, location.state, navigate]);
  const label = destination.isError
    ? "Destination unavailable"
    : destination.data
      ? `${destination.data.city}, ${countryName(destination.data.countryCode)}`
      : destinationId
        ? "Selected Destination"
        : "All Destinations";
  return (
    <DestinationContext.Provider
      value={{
        destinationId,
        label,
        href: (path) => destinationHref(path, destinationId),
        setDestination: (option) => {
          const id = validDestinationId(option?.id);
          void navigate(replaceDestinationContext(location.pathname, location.search, id), {
            replace: true,
            state: null,
          });
        },
      }}
    >
      {children}
    </DestinationContext.Provider>
  );
}

export function DestinationWorkspacePicker() {
  const context = useDestinationContext();
  const { pathname } = useLocation();
  if (
    !context ||
    !["/dashboard", "/destinations", "/experiences", "/partners", "/schedules"].includes(pathname)
  )
    return null;
  return (
    <div className="min-w-0 w-60 max-w-full [&_[data-slot=field]]:max-w-none [&_[data-slot=field]]:flex-row [&_[data-slot=field]]:items-center [&_[data-slot=field-label]]:sr-only">
      <CatalogueReferencePicker
        compact
        id="workspace-destination"
        label="Destination"
        kind="destinations"
        value={context.destinationId}
        initialLabel={context.label}
        emptyLabel="All Destinations"
        onChange={context.setDestination}
      />
    </div>
  );
}
